import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type CompanyFormValues } from '.';
import * as permissionKind from '../../../../../shared/permissions';
import { CompanyTheme } from '../../../../api/types';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import ColorPickerField from '../../../../components/fields/ColorPickerField';
import InputGroup from '../../../../components/fields/InputGroup';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import getPrimaryColorByTheme from '../../../../utilities/getPrimaryColorByTheme';
import hasPermissions from '../../../../utilities/hasPermissions';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import { useCompanyLanguageSelectedContext } from '../CompanyLanguageSelectedProvider';
import useCompanyDetailsTooltip from './useCompanyDetailsTooltip';

const CompanyDetailsForm = () => {
    const { t } = useTranslation(['companyDetails']);
    const languages = useCompanyLanguageSelectedContext();
    const { yesNoSwitch } = useSystemSwitchData();
    const { mfaSettingsTypeOptions, themeOptions } = useSystemOptions();
    const { initialValues, values, setFieldValue } = useFormikContext<CompanyFormValues>();

    const { permissions: accountPermissions } = useAccount();
    const company = useCompany(true);
    const hasPermission = company
        ? hasPermissions(company.permissions, [permissionKind.updateCompany]) &&
          hasPermissions(accountPermissions, [permissionKind.updateCompany])
        : hasPermissions(accountPermissions, [permissionKind.updateCompany]);

    useEffect(() => {
        if (![CompanyTheme.Default, CompanyTheme.Admin].includes(values.theme)) {
            setFieldValue('color', getPrimaryColorByTheme(values.theme)?.substring(1));
        }
    }, [values.theme]);

    const copyrightTooltip = useCompanyDetailsTooltip('fields.copyright.tooltip');

    return (
        <CollapsibleWrapper defaultActiveKey={t('companyDetails:cardTitles.companyDetails')}>
            <Panel
                key={t('companyDetails:cardTitles.companyDetails')}
                header={t('companyDetails:cardTitles.companyDetails')}
            >
                <Row gutter={14}>
                    <Col lg={8} xs={24}>
                        <FormFields.PhoneAndPrefixField
                            {...t('companyDetails:fields.phone', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="phone"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('companyDetails:fields.email', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="email"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('companyDetails:fields.address', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="address"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <TranslatedTextAreaField
                            {...t('companyDetails:fields.description', { returnObjects: true })}
                            disabled={!hasPermission}
                            languages={languages}
                            name="description"
                        />
                    </Col>

                    {[CompanyTheme.Default, CompanyTheme.Admin].includes(values.theme) && (
                        <Col lg={8} xs={24}>
                            <ColorPickerField
                                {...t('companyDetails:fields.color', { returnObjects: true })}
                                disabled={!hasPermission}
                                name="color"
                                required
                            />
                        </Col>
                    )}

                    <Col lg={8} xs={24}>
                        <FormFields.SelectField
                            {...t('companyDetails:fields.theme', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="theme"
                            options={themeOptions}
                            required
                            showSearch
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <TranslatedTextAreaField
                            {...t('companyDetails:fields.copyright', { returnObjects: true })}
                            disabled={!hasPermission}
                            languages={languages}
                            name="copyright"
                            tooltip={copyrightTooltip}
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <SingleUploadField
                            {...t('companyDetails:fields.logo', { returnObjects: true })}
                            disabled={!hasPermission}
                            extensions={allowedExtensions.image}
                            name="logo"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <SingleUploadField
                            {...t('companyDetails:fields.favicon', { returnObjects: true })}
                            disabled={!hasPermission}
                            extensions={allowedExtensions.image}
                            name="favicon"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <SingleUploadField
                            {...t('companyDetails:fields.font', { returnObjects: true })}
                            disabled={!hasPermission}
                            extensions={allowedExtensions.font}
                            name="font"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <SingleUploadField
                            {...t('companyDetails:fields.fontBold', { returnObjects: true })}
                            disabled={!hasPermission}
                            extensions={allowedExtensions.font}
                            name="fontBold"
                        />
                    </Col>
                    <Col lg={8} md={12} xs={24}>
                        <InputGroup {...t('companyDetails:fields.mfa', { returnObjects: true })}>
                            <FormFields.SwitchField
                                {...yesNoSwitch}
                                defaultChecked={initialValues.mfaSettings.enabled}
                                disabled={!hasPermission}
                                name="mfaSettings.enabled"
                            />
                            {values.mfaSettings.enabled && (
                                <FormFields.SelectField
                                    disabled={mfaSettingsTypeOptions.length <= 1 || !hasPermission}
                                    name="mfaSettings.type"
                                    options={mfaSettingsTypeOptions}
                                    showSearch
                                />
                            )}
                        </InputGroup>
                    </Col>
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default CompanyDetailsForm;
